import { useSignal } from "@preact/signals";
import { track } from "../host/telemetry.js";

/**
 * The beta's short survey (P3-03, docs/beta/observation.md): after a room's first finished match in
 * this browser, three one-tap questions. Only the answers are recorded (`feedback_given`, no names
 * and no free text); anything longer goes through the contact form. Answered or dismissed, it
 * doesn't ask again here.
 */
const KEY = "obean.feedback";

/** Whether this browser has answered or dismissed it already (no storage: don't ask). */
export function feedbackDone(storage: Pick<Storage, "getItem"> | null = safe()): boolean {
  try {
    return storage === null || storage.getItem(KEY) !== null;
  } catch {
    return true;
  }
}

function markDone(how: "answered" | "dismissed"): void {
  try {
    localStorage.setItem(KEY, how);
  } catch {
    // Private windows: it may ask again next time, which is fine.
  }
}

function safe(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

const SCALE = [1, 2, 3, 4, 5] as const;
const AGAIN = [
  ["yes", "Yes"],
  ["maybe", "Maybe"],
  ["no", "No"],
] as const;
type Again = (typeof AGAIN)[number][0];

function Choices<T extends string | number>(props: {
  legend: string;
  options: readonly (readonly [T, string])[];
  value: T | null;
  onPick: (v: T) => void;
  hint?: string;
}) {
  return (
    <fieldset class="choices">
      <legend>{props.legend}</legend>
      {props.options.map(([v, label]) => (
        <button
          key={String(v)}
          type="button"
          class="secondary"
          aria-pressed={props.value === v}
          onClick={() => {
            props.onPick(v);
          }}
        >
          {label}
        </button>
      ))}
      {props.hint ? <span class="hint">{props.hint}</span> : null}
    </fieldset>
  );
}

export function FeedbackPrompt({ gameId }: { gameId: string }) {
  const ease = useSignal<number | null>(null);
  const fun = useSignal<number | null>(null);
  const again = useSignal<Again | null>(null);
  const state = useSignal<"asking" | "thanks" | "gone">(feedbackDone() ? "gone" : "asking");
  if (state.value === "gone") return null;
  if (state.value === "thanks")
    return (
      <p class="feedback hint" role="status">
        Thanks! That helps a lot.
      </p>
    );
  const complete = ease.value !== null && fun.value !== null && again.value !== null;
  const scale = SCALE.map((n) => [n, String(n)] as const);
  return (
    <section class="feedback card" aria-labelledby="feedback-title">
      <h3 id="feedback-title">Three quick taps?</h3>
      <Choices
        legend="How easy was it to get into the game together?"
        options={scale}
        value={ease.value}
        onPick={(v) => (ease.value = v)}
        hint="1 hard, 5 easy"
      />
      <Choices
        legend="How much fun was it?"
        options={scale}
        value={fun.value}
        onPick={(v) => (fun.value = v)}
        hint="1 not much, 5 lots"
      />
      <Choices
        legend="Would you play again with the same people?"
        options={AGAIN}
        value={again.value}
        onPick={(v) => (again.value = v)}
      />
      <p class="actions">
        <button
          type="button"
          disabled={!complete}
          onClick={() => {
            track("feedback_given", {
              game_id: gameId,
              ease: ease.value,
              fun: fun.value,
              again: again.value,
            });
            markDone("answered");
            state.value = "thanks";
          }}
        >
          Send
        </button>
        <button
          type="button"
          class="secondary"
          onClick={() => {
            markDone("dismissed");
            state.value = "gone";
          }}
        >
          Not now
        </button>
      </p>
      <p class="hint">
        Anything else? <a href="/contact">Tell us in your own words</a>.
      </p>
    </section>
  );
}
